import api from "../api";
import {
  ModuleResponse,
  ModuleListResponse,
  ModuleStructureResponse,
  ModuleProgressResponse,
  ModuleFormData,
  ModuleFilter,
  ModuleReorderRequest,
  ApiResponse,
} from "../../types/elearning";

class ModuleService {
  private baseUrl = "/elearning/modules";

  // Create new module
  async createModule(moduleData: ModuleFormData): Promise<ModuleResponse> {
    const { courseId, ...data } = moduleData;
    const response = await api.post<ModuleResponse>(
      `/elearning/courses/${courseId}/modules`,
      data
    );
    return response as unknown as ModuleResponse;
  }

  // Get module by ID
  async getModule(
    moduleId: string,
    options?: {
      includeContent?: boolean;
      includeAnalytics?: boolean;
    }
  ): Promise<ModuleResponse> {
    const params = new URLSearchParams();
    if (options?.includeContent) {
      params.append("includeContent", "true");
    }
    if (options?.includeAnalytics) {
      params.append("includeAnalytics", "true");
    }

    const response = await api.get<ModuleResponse>(
      `${this.baseUrl}/${moduleId}?${params.toString()}`
    );

    return response as unknown as ModuleResponse;
  }

  // Get module structure (module + content + assessments)
  async getModuleStructure(moduleId: string): Promise<ModuleStructureResponse> {
    const response = await api.get<ModuleStructureResponse>(
      `${this.baseUrl}/${moduleId}/structure`
    );

    return response as unknown as ModuleStructureResponse;
  }

  // Get modules by course
  async getModulesByCourse(
    courseId: string,
    filters?: ModuleFilter,
    options?: {
      includeContent?: boolean;
    }
  ): Promise<ModuleListResponse> {
    const params = new URLSearchParams({
      ...this.buildFilterParams(filters || {}),
    });

    if (options?.includeContent) {
      params.append("includeContent", "true");
    }

    const response = await api.get<ModuleListResponse>(
      `${this.baseUrl}/course/${courseId}?${params.toString()}`
    );

    return response as unknown as ModuleListResponse;
  }

  // Update module
  async updateModule(
    courseId: string,
    moduleId: string,
    updates: Partial<ModuleFormData>
  ): Promise<ModuleResponse> {
    const response = await api.put<ModuleResponse>(
      `/elearning/courses/${courseId}/modules/${moduleId}`,
      updates
    );

    return response as unknown as ModuleResponse;
  }

  // Delete module
  async deleteModule(
    courseId: string,
    moduleId: string,
    permanent = false
  ): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (permanent) {
      params.append("permanent", "true");
    }

    const response = await api.delete<ApiResponse>(
      `/elearning/courses/${courseId}/modules/${moduleId}?${params.toString()}`
    );

    return response as unknown as ApiResponse;
  }

  // Reorder modules
  async reorderModules(
    courseId: string,
    moduleOrders: ModuleReorderRequest["moduleOrders"]
  ): Promise<ApiResponse> {
    const response = await api.put<ApiResponse>(
      `${this.baseUrl}/course/${courseId}/reorder`,
      { moduleOrders }
    );

    return response as unknown as ApiResponse;
  }

  // Duplicate module
  async duplicateModule(
    moduleId: string,
    options?: {
      title?: string;
      targetCourseId?: string;
    }
  ): Promise<ModuleResponse> {
    const response = await api.post<ModuleResponse>(
      `${this.baseUrl}/${moduleId}/duplicate`,
      options || {}
    );

    return response as unknown as ModuleResponse;
  }

  // Content management within modules

  // Add content to module
  async addContent(moduleId: string, contentId: string): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/${moduleId}/content`,
      { contentId }
    );

    return response as unknown as ApiResponse;
  }

  // Remove content from module
  async removeContent(
    moduleId: string,
    contentId: string
  ): Promise<ApiResponse> {
    const response = await api.delete<ApiResponse>(
      `${this.baseUrl}/${moduleId}/content`,
      { data: { contentId } }
    );

    return response as unknown as ApiResponse;
  }

  // Reorder content within module
  async reorderContent(
    moduleId: string,
    contentOrders: { contentId: string; order: number }[]
  ): Promise<ApiResponse> {
    const response = await api.put<ApiResponse>(
      `${this.baseUrl}/${moduleId}/content/reorder`,
      { contentOrders }
    );

    return response as unknown as ApiResponse;
  }

  // Progress and analytics

  // Get module progress for student
  async getModuleProgress(
    moduleId: string,
    studentId?: string
  ): Promise<ModuleProgressResponse> {
    const params = new URLSearchParams();
    if (studentId) {
      params.append("studentId", studentId);
    }

    const response = await api.get<ModuleProgressResponse>(
      `${this.baseUrl}/${moduleId}/progress?${params.toString()}`
    );

    return response as unknown as ModuleProgressResponse;
  }

  // Get module analytics
  async getModuleAnalytics(moduleId: string): Promise<ApiResponse> {
    const response = await api.get<ApiResponse>(
      `${this.baseUrl}/${moduleId}/analytics`
    );

    return response as unknown as ApiResponse;
  }

  // Check prerequisites
  async checkPrerequisites(
    moduleId: string,
    studentId?: string
  ): Promise<ApiResponse<{ prerequisitesMet: boolean }>> {
    const params = new URLSearchParams();
    if (studentId) {
      params.append("studentId", studentId);
    }

    const response = await api.get<ApiResponse<{ prerequisitesMet: boolean }>>(
      `${this.baseUrl}/${moduleId}/prerequisites?${params.toString()}`
    );

    return response as unknown as ApiResponse<{ prerequisitesMet: boolean }>;
  }

  // Get prerequisite modules
  async getPrerequisites(moduleId: string): Promise<ApiResponse> {
    const response = await api.get<ApiResponse>(
      `${this.baseUrl}/${moduleId}/prerequisites`
    );

    return response as unknown as ApiResponse;
  }

  // Module completion

  // Mark module as started
  async startModule(moduleId: string): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/${moduleId}/start`
    );

    return response as unknown as ApiResponse;
  }

  // Mark module as completed
  async completeModule(moduleId: string): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/${moduleId}/complete`
    );

    return response as unknown as ApiResponse;
  }

  // Reset module progress
  async resetProgress(moduleId: string): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/${moduleId}/reset`
    );

    return response as unknown as ApiResponse;
  }

  // Bulk operations

  // Bulk update modules
  async bulkUpdateModules(
    updates: { moduleId: string; updates: Partial<ModuleFormData> }[]
  ): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/bulk-update`,
      { updates }
    );

    return response as unknown as ApiResponse;
  }

  // Bulk delete modules
  async bulkDeleteModules(
    moduleIds: string[],
    permanent = false
  ): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/bulk-delete`,
      { moduleIds, permanent }
    );

    return response as unknown as ApiResponse;
  }

  // Export module
  async exportModule(
    moduleId: string,
    format: "json" | "scorm" | "pdf"
  ): Promise<ApiResponse> {
    const response = await api.get<ApiResponse>(
      `${this.baseUrl}/${moduleId}/export?format=${format}`,
      { responseType: "blob" }
    );

    return response as unknown as ApiResponse;
  }

  // Import module
  async importModule(file: File, courseId: string): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("courseId", courseId);

    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/import`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response as unknown as ApiResponse;
  }

  // Search modules
  async searchModules(
    searchTerm: string,
    filters?: ModuleFilter
  ): Promise<ModuleListResponse> {
    const params = new URLSearchParams({
      q: searchTerm,
      ...this.buildFilterParams(filters || {}),
    });

    const response = await api.get<ModuleListResponse>(
      `${this.baseUrl}/search?${params.toString()}`
    );

    return response as unknown as ModuleListResponse;
  }

  // Get popular modules
  async getPopularModules(
    courseId?: string,
    limit = 10
  ): Promise<ModuleListResponse> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (courseId) {
      params.append("courseId", courseId);
    }

    const response = await api.get<ModuleListResponse>(
      `${this.baseUrl}/popular?${params.toString()}`
    );

    return response as unknown as ModuleListResponse;
  }

  // Get recommended modules for student
  async getRecommendedModules(
    studentId?: string,
    limit = 5
  ): Promise<ModuleListResponse> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (studentId) {
      params.append("studentId", studentId);
    }

    const response = await api.get<ModuleListResponse>(
      `${this.baseUrl}/recommended?${params.toString()}`
    );

    return response as unknown as ModuleListResponse;
  }

  // Module templates

  // Get module templates
  async getModuleTemplates(): Promise<ModuleListResponse> {
    const response = await api.get<ModuleListResponse>(
      `${this.baseUrl}/templates`
    );

    return response as unknown as ModuleListResponse;
  }

  // Create module from template
  async createFromTemplate(
    templateId: string,
    moduleData: Partial<ModuleFormData>
  ): Promise<ModuleResponse> {
    const response = await api.post<ModuleResponse>(
      `${this.baseUrl}/templates/${templateId}/create`,
      moduleData
    );

    return response as unknown as ModuleResponse;
  }

  // Save module as template
  async saveAsTemplate(
    moduleId: string,
    templateData: {
      name: string;
      description: string;
      category: string;
      isPublic: boolean;
    }
  ): Promise<ApiResponse> {
    const response = await api.post<ApiResponse>(
      `${this.baseUrl}/${moduleId}/save-template`,
      templateData
    );

    return response as unknown as ApiResponse;
  }

  // Utility method to build filter parameters
  private buildFilterParams(filters: ModuleFilter): Record<string, string> {
    const params: Record<string, string> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params[key] = value.join(",");
        } else if (value instanceof Date) {
          params[key] = value.toISOString();
        } else {
          params[key] = value.toString();
        }
      }
    });

    return params;
  }
}

export const moduleService = new ModuleService();
export default moduleService;
